<template>
  <div>
    <div class="mb-3">
      <div class="d-flex align-center" style="gap: 6px; margin: 0 0 8px 0">
        <h3 style="font-size: 16px; font-weight: 700; margin: 0; color: #0f3565">
          {{ $t('Ordens de serviço por responsável') }}
        </h3>
        <v-tooltip color="white" bottom max-width="320">
          <template v-slot:activator="{ on, attrs }">
            <v-icon small color="rgba(0,0,0,0.4)" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
          </template>
          <span class="black--text">
            <p class="mb-0 mt-2" style="font-size: 0.9rem">{{ $t("Apresenta O.S.'s solicitadas e fechadas por responsável dentro do período filtrado.") }}</p>
          </span>
        </v-tooltip>
      </div>
      <div class="d-flex align-center flex-wrap" style="gap: 12px">
        <div class="d-flex align-center" style="gap: 6px">
          <div style="width: 10px; height: 10px; border-radius: 50%; background: #0F3565; flex-shrink: 0" />
          <span style="color: rgba(0,0,0,0.65); font-size: 12px; font-weight: 600">
            {{ $t('Não fechadas') }}
          </span>
        </div>
        <div class="d-flex align-center" style="gap: 6px">
          <div style="width: 10px; height: 10px; border-radius: 50%; background: #BBCF20; flex-shrink: 0" />
          <span style="color: rgba(0,0,0,0.65); font-size: 12px; font-weight: 600">
            {{ $t('Fechadas') }}
          </span>
        </div>
      </div>
    </div>

    <div v-if="loading" class="d-flex justify-center align-center" style="height: 300px">
      <v-progress-circular indeterminate color="#bbcf20" size="40" />
    </div>

    <div v-else-if="isEmpty" class="d-flex flex-column justify-center align-center" style="height: 300px">
      <v-icon color="rgba(0,0,0,0.2)" size="48">mdi-chart-bar</v-icon>
      <span style="color: rgba(0,0,0,0.4); font-size: 13px; margin-top: 8px">
        {{ $t('Nenhum dado encontrado') }}
      </span>
    </div>

    <div v-else class="chart-scroll-wrapper">
      <v-chart
        :option="chartOption"
        :autoresize="false"
        :style="{ height: chartHeight, width: chartWidth }"
      />
    </div>
  </div>
</template>

<script>
import VChart from 'vue-echarts'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { BarChart } from 'echarts/charts'
import { TooltipComponent, GridComponent, DataZoomComponent } from 'echarts/components'
import ApiService from '../../../../services/ApiService'

use([CanvasRenderer, BarChart, TooltipComponent, GridComponent, DataZoomComponent])

const COLOR_NOT_CLOSED = '#0F3565'
const COLOR_CLOSED = '#BBCF20'

export default {
  name: 'ServiceOrdersByResponsible',

  components: { VChart },

  props: {
    filterProp: { type: Object, default: () => ({}) },
  },

  data: () => ({
    loading: false,
    chartOption: {},
    apiService: new ApiService(),
  }),

  computed: {
    isEmpty() {
      return !this.chartOption.series || this.chartOption.series.length === 0
    },
    chartHeight() {
      return '380px'
    },
    chartWidth() {
      const series = this.chartOption.series
      if (!series || !series[0] || !series[0].data) return '100%'
      const count = series[0].data.length
      return count > 15 ? `${count * 60}px` : '100%'
    },
  },

  watch: {
    filterProp: {
      handler() {
        this.load()
      },
      deep: true,
    },
  },

  created() {
    this.load()
  },

  methods: {
    async load() {
      this.loading = true
      try {
        const response = await this.apiService.post(
          'dashboardBI/service-orders-by-responsible',
          this.filterProp
        )
        const { data } = response.content
        this.buildChart(data || [])
      } catch (err) {
        // errors handled globally by ApiService
      } finally {
        this.loading = false
      }
    },

    buildChart(data) {
      if (!data.length) {
        this.chartOption = {}
        return
      }

      const names = data.map((d) => d.responsibleName)
      const closedValues = data.map((d) => d.closed)
      const notClosedValues = data.map((d) => d.notClosed)
this.chartOption = {
        backgroundColor: 'transparent',
        animationDuration: 600,
        animationEasing: 'cubicOut',
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(10,30,60,0.92)',
          borderColor: 'rgba(255,255,255,0.1)',
          textStyle: { color: '#fff', fontSize: 12 },
          axisPointer: { type: 'shadow' },
          formatter: (params) => {
            const name = params[0].axisValue
            const total = params.reduce((sum, p) => sum + (p.value || 0), 0)
            const lines = params
              .map(
                (p) =>
                  `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${p.color};margin-right:5px"></span>${p.seriesName}: <b>${p.value}</b>`
              )
              .join('<br/>')
            return `<b>${name}</b><br/>${lines}<br/><span style="color:rgba(255,255,255,0.7)">Total: <b>${total}</b></span>`
          },
        },
        grid: { top: 8, right: 16, bottom: 60, left: 8, containLabel: true },
        xAxis: {
          type: 'category',
          data: names,
          axisLabel: {
            color: 'rgba(0,0,0,0.6)',
            fontSize: 11,
            interval: 0,
            rotate: names.length > 6 ? 30 : 0,
            width: 80,
            overflow: 'truncate',
          },
          axisLine: { lineStyle: { color: 'rgba(0,0,0,0.15)' } },
          splitLine: { show: false },
        },
        yAxis: {
          type: 'value',
          axisLabel: { color: 'rgba(0,0,0,0.6)', fontSize: 11 },
          axisLine: { show: false },
          splitLine: { lineStyle: { color: 'rgba(0,0,0,0.08)' } },
          minInterval: 1,
        },
        series: [
          {
            name: this.$t('Não fechadas'),
            type: 'bar',
            stack: 'total',
            data: notClosedValues,
            barMaxWidth: 40,
            itemStyle: { color: COLOR_NOT_CLOSED },
            label: {
              show: true,
              position: 'inside',
              color: 'rgba(255,255,255,0.85)',
              fontSize: 10,
              formatter: ({ value }) => (value > 0 ? value : ''),
            },
          },
          {
            name: this.$t('Fechadas'),
            type: 'bar',
            stack: 'total',
            data: closedValues,
            barMaxWidth: 40,
            itemStyle: { color: COLOR_CLOSED, borderRadius: [4, 4, 0, 0] },
            label: {
              show: true,
              position: 'inside',
              color: 'rgba(0,0,0,0.7)',
              fontSize: 10,
              formatter: ({ value }) => (value > 0 ? value : ''),
            },
          },
        ],
      }
    },
  },
}
</script>

<style scoped>
.chart-scroll-wrapper {
  overflow-x: auto;
  background: #ffffff;
}
</style>
